import React, { useEffect, useRef } from 'react';

const AD_CLIENT = 'ca-pub-8379550894300714';

export const AD_SLOTS = {
  LEADERBOARD_728x90: '8911883664',
  SKYSCRAPER_160x600: '3855175712',
  HALF_PAGE_300x600: '9107502395',
  RECTANGLE_300x250: '8444979806',
  RESPONSIVE: '4697306482',
} as const;

interface AdBannerProps {
  adSlot: string;
  style?: React.CSSProperties;
  adFormat?: string;
  fullWidthResponsive?: boolean;
  className?: string;
}

/**
 * AdBanner — renders an <ins> and calls adsbygoogle.push({}) once
 * after the element has been committed to the live DOM by React.
 *
 * useEffect with [] fires after React's commit phase, at which point
 * insRef.current is guaranteed to be a connected DOM node. This is
 * the per-slot equivalent of the inline AdSense snippet:
 *   <ins class="adsbygoogle" ...></ins>
 *   <script>(adsbygoogle = window.adsbygoogle || []).push({});</script>
 *
 * Use key={adKey} from useAdsRefresh() on the parent to force a full
 * unmount+remount on SPA navigation, giving AdSense a fresh unfilled
 * <ins> on every page view.
 */
const AdBanner: React.FC<AdBannerProps> = ({
  adSlot,
  style,
  adFormat,
  fullWidthResponsive = false,
  className = '',
}) => {
  const insRef = useRef<HTMLModElement>(null);
  // Prevents double-push from React StrictMode's double-invocation of useEffect.
  // refs persist across StrictMode's simulated cleanup+remount cycle (same instance),
  // so this guarantees push() is called exactly once per component instance.
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    const el = insRef.current;
    if (!el || !el.isConnected) return;
    hasInitialized.current = true;
    try {
      ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
    } catch {
      // AdSense may throw if blocked by ad-blocker
    }
  }, []); // empty deps — runs once per mount lifecycle

  return (
    <ins
      ref={insRef}
      className={`adsbygoogle${className ? ` ${className}` : ''}`}
      style={style}
      data-ad-client={AD_CLIENT}
      data-ad-slot={adSlot}
      {...(adFormat ? { 'data-ad-format': adFormat } : {})}
      {...(fullWidthResponsive ? { 'data-full-width-responsive': 'true' } : {})}
    />
  );
};

export default AdBanner;
